import { BLOOD_TYPE_SELECT_OPTIONS, type BloodTypeSelectValue } from './bloodType';
import {
  emptyFamilyDiseaseHistory,
  FAMILY_DISEASE_HISTORY_BLOB_LABEL,
  serializeFamilyDiseaseHistory,
  type FamilyDiseaseHistory,
} from './familyDiseaseHistory';

export { BLOOD_TYPE_SELECT_OPTIONS, BLOOD_TYPE_UNKNOWN } from './bloodType';

export const INITIAL_PATIENT_CHECKLIST_VERSION = '2026-06-initial-patient-record';

export type InitialPatientChecklistAnswers = {
  phone: string;
  cpf: string;
  dateOfBirth: string;
  gender: 'M' | 'F' | 'Outro' | '';
  isBrazilian: boolean | '';
  country: string;
  street: string;
  number: string;
  complement: string;
  neighborhood: string;
  city: string;
  state: string;
  zipCode: string;
  allergies: string;
  medications: string;
  preExistingConditions: string;
  familyHistory: FamilyDiseaseHistory;
  previousSurgeries: string;
  lifestyleHabits: string;
  bloodType: BloodTypeSelectValue;
  height: string;
  weight: string;
};

export type InitialChecklistPayload = {
  phone?: string;
  cpf?: string;
  dateOfBirth?: string;
  gender?: 'M' | 'F' | 'Outro';
  isBrazilian?: boolean;
  country?: string;
  street?: string;
  number?: string;
  complement?: string;
  neighborhood?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  medicalHistory?: string;
  allergies?: string;
  medications?: string;
  bloodType?: string;
  height?: number;
  weight?: number;
  checklistVersion?: string;
};

export const initialPatientChecklistDefaults: InitialPatientChecklistAnswers = {
  phone: '',
  cpf: '',
  dateOfBirth: '',
  gender: '',
  isBrazilian: '',
  country: '',
  street: '',
  number: '',
  complement: '',
  neighborhood: '',
  city: '',
  state: '',
  zipCode: '',
  allergies: '',
  medications: '',
  preExistingConditions: '',
  familyHistory: emptyFamilyDiseaseHistory(),
  previousSurgeries: '',
  lifestyleHabits: '',
  bloodType: '',
  height: '',
  weight: '',
};

export const initialPatientChecklistSteps = [
  {
    id: 'personal',
    title: 'Dados pessoais',
    requiredFields: ['phone', 'cpf', 'dateOfBirth', 'gender', 'isBrazilian'],
  },
  {
    id: 'address',
    title: 'Endereco',
    requiredFields: ['street', 'number', 'neighborhood', 'city', 'state', 'zipCode'],
  },
  {
    id: 'clinical',
    title: 'Historico clinico',
    requiredFields: ['allergies', 'medications', 'preExistingConditions', 'height', 'weight'],
  },
  {
    id: 'background',
    title: 'Antecedentes e habitos',
    requiredFields: ['familyHistory', 'previousSurgeries', 'lifestyleHabits'],
  },
] as const;

const clinicalLabelMap: Array<[keyof InitialPatientChecklistAnswers, string]> = [
  ['preExistingConditions', 'Doencas pre-existentes'],
  ['familyHistory', FAMILY_DISEASE_HISTORY_BLOB_LABEL],
  ['previousSurgeries', 'Cirurgias anteriores'],
  ['lifestyleHabits', 'Habitos de vida'],
];

export function buildInitialChecklistPayload(
  answers: InitialPatientChecklistAnswers,
): InitialChecklistPayload {
  const medicalHistory = clinicalLabelMap
    .map(([field, label]) => {
      if (field === 'familyHistory') {
        return `${label}: ${serializeFamilyDiseaseHistory(answers.familyHistory)}`;
      }
      const val = answers[field];
      const strVal = typeof val === 'string' ? val.trim() : '';
      return `${label}: ${strVal || 'Nao informado'}`;
    })
    .join('\n');

  // Converte a data do formato YYYY-MM-DD para um formato ISO 8601 completo (incluindo tempo)
  // para passar na validação rigorosa do backend (class-validator @IsISO8601)
  let isoDateOfBirth = answers.dateOfBirth;
  if (answers.dateOfBirth && answers.dateOfBirth.length === 10) {
    isoDateOfBirth = new Date(answers.dateOfBirth).toISOString();
  }

  return {
    phone: answers.phone.trim(),
    cpf: answers.cpf.trim(),
    dateOfBirth: isoDateOfBirth,
    gender: answers.gender || undefined,
    isBrazilian: answers.isBrazilian === '' ? undefined : answers.isBrazilian,
    country: answers.country.trim() || undefined,
    street: answers.street.trim(),
    number: answers.number.trim(),
    complement: answers.complement.trim(),
    neighborhood: answers.neighborhood.trim(),
    city: answers.city.trim(),
    state: answers.state.trim().toUpperCase(),
    zipCode: answers.zipCode.trim(),
    allergies: answers.allergies.trim(),
    medications: answers.medications.trim(),
    medicalHistory,
    bloodType: answers.bloodType || undefined,
    height: answers.height ? parseFloat(answers.height) : undefined,
    weight: answers.weight ? parseFloat(answers.weight) : undefined,
    checklistVersion: INITIAL_PATIENT_CHECKLIST_VERSION,
  };
}
